import {
  Button,
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  LoadingSpinner,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  useToast,
} from '@ais-ui/ui-core';
import { PiPlusThin } from 'react-icons/pi';
import React, { useEffect, useMemo, useState } from 'react';
import { useSettings } from '../../../Settings/SettingsProvider';
import {
  LegendColor,
  useMapLegendColors,
  useSaveMapLegendsToSettings,
} from '@ais-ui/websockets';
import { ShipList } from '@ais-ui/api-spec/config';
import { useFetchAllShiplists } from '@ais-ui/api';
import { CheckIcon } from '@radix-ui/react-icons';
import { LegendItem } from '../ColorTargetsBy';
import { useReplayContext } from '../../../Replay/ReplayContext';

const ToggleShiplist = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { settings } = useSettings();
  const { data, isLoading } = useFetchAllShiplists();

  const { isPlaying } = useReplayContext();
  const { mutate } = useSaveMapLegendsToSettings(isPlaying);
  const { toast } = useToast();

  const shiplists = data?.records ? data.records : [];

  const shownShiplistsId = useMemo(() => {
    return settings?.['ribbonPlugin.settings']?.legend_shiplists || [];
  }, [settings]);
  const onToggleShiplist = (shiplist: ShipList) => {
    setIsSubmitting(true);
    const currentShiplists =
      settings['ribbonPlugin.settings'].legend_shiplists || [];
    let wasAdded = false;

    const updatedShiplists = currentShiplists.includes(shiplist.id)
      ? currentShiplists.filter((id: string) => id !== shiplist.id)
      : [...currentShiplists, shiplist.id];

    if (!currentShiplists.includes(shiplist.id)) {
      wasAdded = true;
    }

    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        legend_shiplists: updatedShiplists,
      },
    };

    mutate(
      {
        settings: settingsToSave,
      },
      {
        onSuccess: () => {
          toast({
            className: 'apply-shiplist-success-toast',
            title: 'Success!',
            description: wasAdded
              ? 'Shiplist added successfully'
              : 'Shiplist removed successfully',
          });
          setIsSubmitting(false);
        },
        onError: () => {
          toast({
            className: 'apply-shiplist-error-toast',
            title: 'Error!',
            description: wasAdded
              ? 'Error adding shiplist'
              : 'Error removing shiplist',
            variant: 'destructive',
          });
          setIsSubmitting(false);
        },
      },
    );
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" size="icon" variant="outline">
          <PiPlusThin className="size-4"></PiPlusThin>
        </Button>
      </PopoverTrigger>
      <PopoverContent side="right">
        <Command>
          <CommandInput placeholder="Find shiplist"></CommandInput>

          <CommandList className="w-full">
            <CommandGroup heading="Shiplists">
              <ScrollArea className="h-44 pr-4">
                {isLoading && <LoadingSpinner size="small"></LoadingSpinner>}
                {!isLoading &&
                  shiplists.map((shiplist: ShipList) => (
                    <CommandItem
                      key={shiplist.id}
                      className="w-60 max-w-full"
                      onSelect={() => onToggleShiplist(shiplist)}
                      disabled={isSubmitting}
                    >
                      <div className="flex justify-between w-full pr-2">
                        <div className="flex space-x-2 items-center">
                          <div
                            className="h-2 w-2 rounded-full"
                            style={{ backgroundColor: `#${shiplist.color}` }}
                          ></div>
                          <div>{shiplist.name}</div>
                        </div>
                        {shownShiplistsId.includes(shiplist.id) && (
                          <CheckIcon className="size-4" />
                        )}
                      </div>
                    </CommandItem>
                  ))}
              </ScrollArea>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const ColorByShipList = () => {
  const { settings } = useSettings();
  const { data, isLoading } = useFetchAllShiplists();
  const { isPlaying } = useReplayContext();
  const { mutate } = useSaveMapLegendsToSettings(isPlaying);
  const { toast } = useToast();
  const shiplists = data?.records ? data.records : [];

  const [displayLegendItems, setDisplayLegendItems] = useState<LegendColor[]>(
    [],
  );

  const { data: legendColors } = useMapLegendColors();

  const validColors = useMemo(() => {
    return legendColors?.filter((item) => Boolean(item.color)) || [];
  }, [legendColors]);

  useEffect(() => {
    // Sort validColors alphabetically by name before setting to state
    const sortedValidColors = [...validColors].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
    setDisplayLegendItems(sortedValidColors);
  }, [validColors]);

  const onRemove = (event: React.MouseEvent, category: string) => {
    event.stopPropagation(); // Prevent click event from propagating to the parent
    const matchingShiplist = shiplists.find(
      (shiplist) => shiplist.name === category,
    );

    if (!matchingShiplist) {
      toast({
        title: 'Error!',
        description: 'Error removing shiplist',
        variant: 'destructive',
      });
      return;
    }

    const currentShiplists =
      settings['ribbonPlugin.settings'].legend_shiplists || [];

    const updatedShiplists = currentShiplists.filter(
      (shiplistId: string) => shiplistId !== matchingShiplist.id,
    );

    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        legend_shiplists: updatedShiplists,
      },
    };

    mutate(
      {
        settings: settingsToSave,
      },
      {
        onSuccess: () => {
          toast({
            className: 'apply-shiplist-success-toast',
            title: 'Success!',
            description: 'Shiplist removed successfully',
          });
        },
        onError: () => {
          toast({
            className: 'apply-shiplist-error-toast',
            title: 'Error!',
            description: 'Error removing shiplist',
            variant: 'destructive',
          });
        },
      },
    );
  };
  return (
    <div className="p-4 grid grid-cols-3 gap-4 bg-background">
      {displayLegendItems.map((item) => {
        const matchingShiplist = shiplists.find(
          (shiplist) => shiplist.name === item.name,
        );
        let id;
        if (item.name === 'Others') {
          id = item.name;
        } else {
          id = matchingShiplist?.id;
        }
        return (
          <LegendItem
            key={item.name}
            category={item.name}
            color={item.color}
            removable={item.name !== 'Others'}
            onRemove={onRemove}
            id={id}
          />
        );
      })}
      <ToggleShiplist></ToggleShiplist>
    </div>
  );
};
